import currency from 'currency.js';

/**
 * Format a numeric value for display based on currency type
 * @param value - The numeric value to format
 * @param currencyType - 'USD' or 'ARS' to determine format
 * @returns Formatted string for display
 * 
 * @example
 * formatCurrency(1234.56, 'USD') // "1,234.56"
 * formatCurrency(1234.56, 'ARS') // "1.234,56"
 */
export function formatCurrency(value: number | string, currencyType: string): string {
  // 1) Normalize input to a numeric value safely
  let numericValue = 0;
  if (typeof value === 'number') {
    numericValue = isFinite(value) ? value : 0;
  } else if (typeof value === 'string') {
    const trimmed = value.trim();
    // Try native parse first for standard numeric strings
    const direct = Number(trimmed);
    if (!Number.isNaN(direct)) {
      numericValue = direct;
    } else {
      // Fallback: parse using locale-aware rules based on currency type
      try {
        numericValue = parseCurrencyInput(trimmed, currencyType);
      } catch {
        numericValue = 0;
      }
    }
  }

  // 2) Build a currency instance using a neutral parse (number) and strict precision
  const instance = currency(numericValue, { precision: 2 });

  // 3) Format for display based on target currency format
  const formatOptions = currencyType === 'USD'
    ? { symbol: '', decimal: '.', separator: ',' }
    : { symbol: '', decimal: ',', separator: '.' };

  return instance.format(formatOptions);
}

/**
 * Parse user input to numeric value based on currency type
 * @param input - User input string (e.g., "1,234.56" or "1.234,56")
 * @param currencyType - 'USD' or 'ARS' to determine expected format
 * @returns Parsed numeric value for backend storage
 * @throws Error if input cannot be parsed as valid number
 * 
 * @example
 * parseCurrencyInput("1,234.56", "USD") // 1234.56
 * parseCurrencyInput("1.234,56", "ARS") // 1234.56
 */
export function parseCurrencyInput(input: string | number, currencyType: string): number {
  if (typeof input === 'number') {
    // Normalize to 2-decimals via currency.js
    return currency(input, { precision: 2 }).value;
  }

  const raw = String(input ?? '').trim();
  const options = currencyType === 'USD'
    ? { decimal: '.', separator: ',', precision: 2, errorOnInvalid: true }
    : { decimal: ',', separator: '.', precision: 2, errorOnInvalid: true };

  // currency.js handles negatives (e.g., -$1, ($1)) and thousand separators
  return currency(raw, options).value;
}

/**
 * Get currency formatting options based on currency type
 * @param currencyType - 'USD' or 'ARS'
 * @returns Currency.js options object
 * 
 * @example
 * getCurrencyOptions('USD') // { symbol: '', decimal: '.', separator: ',' }
 * getCurrencyOptions('ARS') // { symbol: '', decimal: ',', separator: '.' }
 */
export function getCurrencyOptions(currencyType: string) {
  return currencyType === 'USD'
    ? { symbol: '', decimal: '.', separator: ',', precision: 2 }
    : { symbol: '', decimal: ',', separator: '.', precision: 2 };
}

/**
 * Format currency with symbol for display
 * @param value - The numeric value to format
 * @param currencyType - 'USD' or 'ARS' to determine format and symbol
 * @returns Formatted string with currency symbol
 * 
 * @example
 * formatCurrencyWithSymbol(1234.56, 'USD') // "$1,234.56"
 * formatCurrencyWithSymbol(1234.56, 'ARS') // "$1.234,56"
 */
export function formatCurrencyWithSymbol(value: number | string, currencyType: string): string {
  // Normalize similarly to formatCurrency
  let numericValue = 0;
  if (typeof value === 'number') {
    numericValue = isFinite(value) ? value : 0;
  } else if (typeof value === 'string') {
    const trimmed = value.trim();
    const direct = Number(trimmed);
    if (!Number.isNaN(direct)) {
      numericValue = direct;
    } else {
      try {
        numericValue = parseCurrencyInput(trimmed, currencyType);
      } catch {
        numericValue = 0;
      }
    }
  }

  const symbol = '$'; // Both ARS and USD use '$' symbol, code labeling handled by caller when needed
  const instance = currency(numericValue, { precision: 2 });
  const formatOptions = currencyType === 'USD'
    ? { symbol, decimal: '.', separator: ',' }
    : { symbol, decimal: ',', separator: '.' };

  return instance.format(formatOptions);
}

/**
 * Format currency and append ISO code when desired (e.g., "1,234.56 USD").
 */
export function formatCurrencyWithCode(value: number | string, currencyType: string): string {
  const base = formatCurrency(value, currencyType);
  return `${base} ${currencyType}`;
}

/**
 * Normalize mixed numeric inputs into a two-decimal number or null
 * Accepts numbers, numeric strings, and empty values gracefully
 */
export function normalizeCurrencyValue(
  value: number | string | null | undefined,
  opts: { precision?: number } = {},
): number | null {
  const precision = opts.precision ?? 2;

  if (value === null || value === undefined) return null;
  if (typeof value === 'number') {
    if (Number.isNaN(value)) return null;
    return currency(value, { precision }).value;
  }

  const trimmed = String(value).trim();
  if (trimmed === '') return null;

  try {
    return currency(trimmed, { precision, errorOnInvalid: true }).value;
  } catch {
    return null;
  }
}
